import { Box, IconButton, Typography, TextField, Button, CircularProgress } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import { PureLightThemeCaaS } from 'src/theme/schemes/PureLightTheme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { sendContactEmail } from 'src/api/sendContactEmail';
// Typeform Report a Bug (kept for rollback) — same form ID as Contact Us
// import { createWidget } from '@typeform/embed';
// import '@typeform/embed/build/css/widget.css';
import { WidgetContext } from 'src/contexts/WidgetContext';
import { useAuth0 } from '@auth0/auth0-react';
import ContactUsFormFlow from './ContactUsFormFlow';

interface ReportBugProps {
    handleBack: () => void;
    handleClose: () => void;
}

type FormState = 'form' | 'error' | 'success';

const ReportBug = ({ handleBack, handleClose }: ReportBugProps) => {
    const { NON_LOGGED_IN_CONFIG, isLoggedInAuth0ExperienceGlobal } = useContext(WidgetContext);
    const { user, getAccessTokenSilently, isAuthenticated: auth0IsAuthenticated } = useAuth0();
    const authId = user?.sub || NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID;
    const [accessAuthToken, setAccessAuthToken] = useState<string | null>(null);
    // Typeform widget ref
    // const typeformContainerRef = useRef<HTMLDivElement>(null);
    // const typeformWidgetRef = useRef<any>(null);

    const [formState, setFormState] = useState<FormState>('form');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        message: '',
    });
    const [errors, setErrors] = useState({
        firstName: false,
        lastName: false,
        email: false,
        message: false,
    });
    const [errorMessage, setErrorMessage] = useState('');

    // Typeform form ID
    // const typeformId = "vxmwTLNV";

    useEffect(() => {
        const loadToken = async () => {
            try {
                const token = auth0IsAuthenticated
                    ? await getAccessTokenSilently()
                    : !isLoggedInAuth0ExperienceGlobal
                        ? NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_ACCESS_TOKEN
                        : await getAccessTokenSilently();
                setAccessAuthToken(token);
            } catch {
                setAccessAuthToken(NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_ACCESS_TOKEN);
            }
        };
        void loadToken();
    }, [auth0IsAuthenticated, getAccessTokenSilently, isLoggedInAuth0ExperienceGlobal, NON_LOGGED_IN_CONFIG]);

    // Initialize Typeform widget when component mounts
    // useEffect(() => {
    //     if (typeformId && typeformContainerRef.current) {
    //         // Clean up existing widget if it exists
    //         if (typeformWidgetRef.current) {
    //             try {
    //                 typeformWidgetRef.current.unmount();
    //                 typeformWidgetRef.current = null;
    //             } catch (error) {
    //                 console.error('Error unmounting existing widget:', error);
    //             }
    //         }
    //
    //         // Small delay to ensure container is ready
    //         const timer = setTimeout(() => {
    //             if (typeformContainerRef.current) {
    //                 try {
    //                     // Use hidden field to skip welcome screen
    //                     // This requires setting up a logic jump in Typeform:
    //                     // 1. Add a hidden field named "skip_welcome"
    //                     // 2. Set up logic: if skip_welcome = "true", jump to first question
    //                     typeformWidgetRef.current = createWidget(typeformId, {
    //                         container: typeformContainerRef.current,
    //                         height: 500,
    //                         inlineOnMobile: true,
    //                         hideHeaders: true,
    //                         hideFooter: true,
    //                         hidden: {
    //                             skip_welcome: 'true',
    //                             auth_id: authId || 'unknown',
    //                             section: 'report_bug',
    //                         },
    //                         onSubmit: () => {
    //                             console.log('Typeform submitted');
    //                             // Auto-return to HelpMenu after 3 seconds
    //                             setTimeout(() => {
    //                                 handleBack();
    //                             }, 3000);
    //                         },
    //                     });
    //                 } catch (error) {
    //                     console.error('Error creating Typeform widget:', error);
    //                 }
    //             }
    //         }, 200);
    //
    //         // Cleanup function to unmount widget
    //         return () => {
    //             clearTimeout(timer);
    //             if (typeformWidgetRef.current) {
    //                 try {
    //                     typeformWidgetRef.current.unmount();
    //                     typeformWidgetRef.current = null;
    //                 } catch (error) {
    //                     console.error('Error unmounting Typeform widget:', error);
    //                 }
    //             }
    //         };
    //     }
    // }, [typeformId, authId]);

    // Auto-return to menu after 5 seconds on success
    useEffect(() => {
        if (formState === 'success') {
            const timer = setTimeout(() => {
                handleBack();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [formState, handleBack]);

    const validateEmail = (email: string): boolean => {
        return email.includes('@') && email.trim().length > 0;
    };

    const validateForm = (): boolean => {
        const emailTrimmed = formData.email.trim();
        const emailHasText = emailTrimmed.length > 0;
        const emailHasAt = emailTrimmed.includes('@');

        const newErrors = {
            firstName: !formData.firstName.trim(),
            lastName: !formData.lastName.trim(),
            email: !emailHasText || !emailHasAt,
            message: !formData.message.trim(),
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some(error => error);

        if (hasErrors) {
            // Check if email has text but no @ symbol
            if (emailHasText && !emailHasAt) {
                setErrorMessage('Please add a valid email');
            } else {
                setErrorMessage('Complete required fields below:');
            }
            setFormState('error');
            return false;
        }

        setErrorMessage('');
        return true;
    };

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear error for this field when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({ ...prev, [field]: false }));
        }

        // Clear error message if all fields are valid
        if (errorMessage && value.trim()) {
            if (field === 'email' && validateEmail(value)) {
                setErrorMessage('');
            } else if (field !== 'email') {
                setErrorMessage('');
            }
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setFormState('form');

        try {
            await sendContactEmail(formData);
            setFormState('success');
        } catch (error) {
            setFormState('error');
            setErrorMessage('Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "100%", width: "100%", bgcolor: "#EDEDED" }}>
            {/* Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,

                    backgroundColor: "#EDEDED",
                    borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.white[10]}`,
                }}
            >
                {/* <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={0.5}>
                    <IconButton
                        aria-label="back"
                        onClick={handleBack}
                    >
                        <ArrowBackIcon sx={{ color: PureLightThemeCaaS.colors.alpha.black[100] }} />
                    </IconButton>
                    <Typography variant="h4" fontSize={"16px"} color={PureLightThemeCaaS.colors.alpha.black[100]}>
                        Report a Bug
                    </Typography>
                    <IconButton aria-label="close" onClick={handleClose} sx={{
                        visibility: "hidden"
                    }}>
                        <CloseIcon sx={{ color: PureLightThemeCaaS.colors.alpha.black[100] }} />
                    </IconButton>
                </Box> */}
            </Box>


            {/* Content */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "hidden",
                    minHeight: 0,
                    width: "100%",
                    bgcolor: "#EDEDED",
                    display: "flex",
                    flexDirection: "column",
                    // px: {
                    //     xs: 2,
                    //     md: 5,
                    // },
                    // py: 2,
                }}
            >
                {/* Typeform Widget Container
                {typeformId ? (
                    <Box
                        ref={typeformContainerRef}
                        sx={{
                            width: "100%",
                            flex: 1,
                            minHeight: 500,
                            bgcolor: "transparent",
                            display: "flex",
                            flexDirection: "column",
                            '& iframe': {
                                border: 'none',
                                width: '100%',
                                height: '100%',
                                flex: 1,
                            },
                            '& .tf-v1-widget': {
                                width: '100%',
                                height: '100%',
                                flex: 1,
                            },
                        }}
                    />
                ) : ( */}
                <ContactUsFormFlow
                    accessAuthToken={accessAuthToken}
                    onFinished={handleBack}
                    section="report_bug"
                />
                {false && (
                    // Original Form Code - Kept as fallback
                    <>
                        {formState === 'success' ? (
                            // Success State
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: "400px",
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: { xs: "24px", md: "32px" },
                                        fontWeight: "bold",
                                        color: PureLightThemeCaaS.colors.alpha.black[100],
                                        pb: 2,
                                    }}
                                >
                                    Message Sent
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: "14px", md: "16px" },
                                        color: PureLightThemeCaaS.colors.alpha.black[100],
                                        maxWidth: "500px",
                                    }}
                                >
                                    Thanks for reaching out — we've received your message. Our team will review it and get back to you as soon as possible.
                                </Typography>
                            </Box>
                        ) : (
                            // Form State
                            <Box>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: "14px", md: "16px" },
                                        color: PureLightThemeCaaS.colors.alpha.black[100],
                                        pb: 1,
                                    }}
                                >
                                    Complete the form below to reach out to our customer services team. Please allow xx hours for a response.
                                </Typography>

                                {/* Reserved space for error message - always takes space, shows content only when error */}
                                <Box sx={{ minHeight: "35px", pb: 1 }}>
                                    {formState === 'error' && errorMessage && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "14px",
                                                color: PureLightThemeCaaS.colors.alpha.orange[100],
                                                fontWeight: "medium",
                                            }}
                                        >
                                            {errorMessage}
                                        </Typography>
                                    )}
                                </Box>

                                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <TextField
                                        label="First Name"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={formData.firstName}
                                        onChange={handleInputChange('firstName')}
                                        size="small"
                                        InputLabelProps={{
                                            sx: {
                                                color: errors.firstName && formState === 'error'
                                                    ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                    : PureLightThemeCaaS.colors.alpha.black[100],
                                                "&.Mui-focused": {
                                                    color: errors.firstName && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : PureLightThemeCaaS.colors.alpha.black[100],
                                                },
                                                "& .MuiInputLabel-asterisk": {
                                                    color: errors.firstName && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : "inherit",
                                                },
                                            },
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
                                                "& fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                                },
                                            },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Last Name"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={formData.lastName}
                                        onChange={handleInputChange('lastName')}
                                        size="small"
                                        InputLabelProps={{
                                            sx: {
                                                color: errors.lastName && formState === 'error'
                                                    ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                    : PureLightThemeCaaS.colors.alpha.black[100],
                                                "&.Mui-focused": {
                                                    color: errors.lastName && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : PureLightThemeCaaS.colors.alpha.black[100],
                                                },
                                                "& .MuiInputLabel-asterisk": {
                                                    color: errors.lastName && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : "inherit",
                                                },
                                            },
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
                                                "& fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                                },
                                            },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Email"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        type="text"
                                        value={formData.email}
                                        onChange={handleInputChange('email')}
                                        size="small"
                                        InputLabelProps={{
                                            sx: {
                                                color: errors.email && formState === 'error'
                                                    ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                    : PureLightThemeCaaS.colors.alpha.black[100],
                                                "&.Mui-focused": {
                                                    color: errors.email && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : PureLightThemeCaaS.colors.alpha.black[100],
                                                },
                                                "& .MuiInputLabel-asterisk": {
                                                    color: errors.email && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : "inherit",
                                                },
                                            },
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
                                                "& fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                                },
                                            },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Message"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        multiline
                                        rows={4}
                                        value={formData.message}
                                        onChange={handleInputChange('message')}
                                        size="small"
                                        InputLabelProps={{
                                            sx: {
                                                color: errors.message && formState === 'error'
                                                    ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                    : PureLightThemeCaaS.colors.alpha.black[100],
                                                "&.Mui-focused": {
                                                    color: errors.message && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : PureLightThemeCaaS.colors.alpha.black[100],
                                                },
                                                "& .MuiInputLabel-asterisk": {
                                                    color: errors.message && formState === 'error'
                                                        ? PureLightThemeCaaS.colors.alpha.orange[100]
                                                        : "inherit",
                                                },
                                            },
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
                                                "& fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                                },
                                            },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[20]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]} !important`,
                                            },
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: `${PureLightThemeCaaS.colors.alpha.black[50]} !important`,
                                            },
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting}
                                        sx={{
                                            py: 0.5,
                                            fontSize: "16px",
                                            fontWeight: "medium",
                                            width: "70%",
                                            margin: "0 auto",
                                            mt: 1,
                                            borderRadius: 3,
                                            bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                                            color: PureLightThemeCaaS.colors.alpha.white[100],
                                            "&:hover": {
                                                bgcolor: PureLightThemeCaaS.colors.alpha.orange[90],
                                            },
                                            "&:disabled": {
                                                bgcolor: PureLightThemeCaaS.colors.alpha.orange[50],
                                            },
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <CircularProgress size={24} sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }} />
                                        ) : (
                                            "Submit"
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
                {/* )} */}
            </Box>
        </Box>
    );
};

export default ReportBug;
